/**
 * Role-based permissions utilities for FMiQ-One frontend
 * 
 * Role Hierarchy (lowest to highest):
 * 1. requester      - Submit WO requests, view own requests only
 * 2. technician     - L1 + Start/stop work, log time, add materials, update WO status
 * 3. field_surveyor - L2 + FCA data capture, asset documentation, assessments
 * 4. sme            - L3 + Review/approve assessments, quality control
 * 5. planner        - L4 + Schedule work, assign technicians, PM schedules, reports
 * 6. admin          - L5 + Manage users in org, full access (within purchased modules)
 * 7. super_admin    - L6 + Multi-client access, cross-org management
 * 8. founder        - L7 + System-wide settings, feature flags, all clients
 * 
 * Each level inherits all permissions from levels below it.
 */

// Role hierarchy mapping
const ROLE_HIERARCHY = {
  requester: 1,
  technician: 2,
  field_surveyor: 3,
  inspector: 3,       // Legacy alias
  surveyor: 3,        // Legacy alias
  sme: 4,
  planner: 5,
  facility_manager: 5,  // Legacy alias
  admin: 6,
  client_admin: 6,    // Legacy alias
  super_admin: 7,
  founder: 8,
};

// Role display names
const ROLE_DISPLAY_NAMES = {
  requester: 'Requester',
  technician: 'Technician',
  field_surveyor: 'Field Surveyor',
  inspector: 'Field Surveyor',
  surveyor: 'Field Surveyor',
  sme: 'Subject Matter Expert',
  planner: 'Planner',
  facility_manager: 'Planner',
  admin: 'Administrator',
  client_admin: 'Administrator',
  super_admin: 'Super Administrator',
  founder: 'Founder',
};

/**
 * Get the hierarchy level for a role
 * @param {string} role - The role name
 * @returns {number} - The hierarchy level (higher = more permissions)
 */
export const getRoleLevel = (role) => {
  return ROLE_HIERARCHY[role] || 0;
};

/**
 * Check if user's role meets or exceeds the required role level
 * @param {string} userRole - The user's current role
 * @param {string} requiredRole - The minimum required role
 * @returns {boolean}
 */
export const hasMinimumRole = (userRole, requiredRole) => {
  return getRoleLevel(userRole) >= getRoleLevel(requiredRole);
};

/**
 * Get human-readable display name for a role
 * @param {string} role - The role name
 * @returns {string}
 */
export const getRoleDisplayName = (role) => {
  return ROLE_DISPLAY_NAMES[role] || role?.replace('_', ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
};

// ============ Convenience Functions ============

export const isFounder = (role) => role === 'founder';

export const isSuperAdmin = (role) => hasMinimumRole(role, 'super_admin');

export const isAdmin = (role) => hasMinimumRole(role, 'admin');

export const isPlannerOrAbove = (role) => hasMinimumRole(role, 'planner');

export const isSmeOrAbove = (role) => hasMinimumRole(role, 'sme');

export const isSurveyorOrAbove = (role) => hasMinimumRole(role, 'field_surveyor');

export const isTechnicianOrAbove = (role) => hasMinimumRole(role, 'technician');

// ============ Permission Checks ============

/**
 * Check if user can manage users (invite, edit, delete)
 */
export const canManageUsers = (role) => isAdmin(role);

/**
 * Check if user can create/delete clients and facilities
 */
export const canManageOrganization = (role) => isAdmin(role);

/**
 * Check if user can access the Review Queue
 */
export const canAccessReviewQueue = (role) => isSmeOrAbove(role);

/**
 * Check if user can create work orders
 */
export const canCreateWorkOrders = (role) => isPlannerOrAbove(role);

/**
 * Check if user can assign work orders to technicians
 */
export const canAssignWorkOrders = (role) => isPlannerOrAbove(role);

/**
 * Check if user can update work order status (technicians and above)
 */
export const canUpdateWorkOrderStatus = (role) => isTechnicianOrAbove(role);

/**
 * Check if user can create/edit assessments
 */
export const canCreateAssessments = (role) => isSurveyorOrAbove(role);

/**
 * Check if user can approve/reject assessments
 */
export const canApproveAssessments = (role) => isSmeOrAbove(role);

/**
 * Check if user can view admin sections
 */
export const canViewAdminSection = (role) => isAdmin(role);

/**
 * Check if user can view work orders
 */
export const canViewWorkOrders = (role) => isTechnicianOrAbove(role);

/**
 * Check if user can view the Work Order Map (planning feature)
 */
export const canViewWorkOrderMap = (role) => isPlannerOrAbove(role);

/**
 * Check if user can manage PM Schedules
 */
export const canManagePMSchedules = (role) => isPlannerOrAbove(role);

/**
 * Check if user can manage vendors
 */
export const canManageVendors = (role) => isPlannerOrAbove(role);

// ============ Navigation Helpers ============

/**
 * Get allowed navigation items based on user role and feature flags
 * @param {string} role - User's role
 * @param {object} features - Feature flags object
 * @returns {object} - Object with navigation item keys and boolean values
 */
export const getAllowedNavigation = (role, features = {}) => {
  const fcaEnabled = features.fca_enabled !== false;
  const iwmsEnabled = features.iwms_enabled === true;

  return {
    // Always visible
    dashboard: true,
    
    // General (Level 3+ - Field Surveyor and above)
    clients: isSurveyorOrAbove(role),
    facilities: isSurveyorOrAbove(role),
    
    // FCA Module
    assessments: fcaEnabled && isSurveyorOrAbove(role),
    reviewQueue: fcaEnabled && isSmeOrAbove(role),
    portfolioMap: fcaEnabled && isSurveyorOrAbove(role),
    inspectionPlanner: fcaEnabled && isSurveyorOrAbove(role),
    regionalAnalysis: fcaEnabled && isSurveyorOrAbove(role),
    fcaAdmin: fcaEnabled && isAdmin(role),
    
    // IWMS Module
    workOrders: iwmsEnabled && isTechnicianOrAbove(role),
    workOrderMap: iwmsEnabled && isPlannerOrAbove(role),
    pmSchedules: iwmsEnabled && isSurveyorOrAbove(role),
    warranties: iwmsEnabled && isSurveyorOrAbove(role),
    vendors: iwmsEnabled && isPlannerOrAbove(role),
    leases: iwmsEnabled && isSurveyorOrAbove(role),
    iwmsAdmin: iwmsEnabled && isAdmin(role),
    
    // Admin
    organization: isAdmin(role),
  };
};

export default {
  getRoleLevel,
  hasMinimumRole,
  getRoleDisplayName,
  isFounder,
  isSuperAdmin,
  isAdmin,
  isPlannerOrAbove,
  isSmeOrAbove,
  isSurveyorOrAbove,
  isTechnicianOrAbove,
  canManageUsers,
  canManageOrganization,
  canAccessReviewQueue,
  canCreateWorkOrders,
  canAssignWorkOrders,
  canUpdateWorkOrderStatus,
  canCreateAssessments,
  canApproveAssessments,
  canViewAdminSection,
  canViewWorkOrders,
  canViewWorkOrderMap,
  canManagePMSchedules,
  canManageVendors,
  getAllowedNavigation,
};
